import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/FirebaseAuthContext';
import { useUsage } from '../contexts/UsageContext';
import { LoadingSpinner } from './LoadingSpinner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import '../styles/chat-side-panel.css';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface ChatSidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  problemId: string;
  problemTitle: string;
}

export const ChatSidePanel: React.FC<ChatSidePanelProps> = ({
  isOpen,
  onClose,
  problemId,
  problemTitle,
}) => {
  const { isAuthenticated } = useAuth();
  const { openPaywall, refreshUsage } = useUsage();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [, setHasUnsavedChanges] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [copiedCodeId, setCopiedCodeId] = useState<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const loadChatHistory = useCallback(async () => {
    try {
      setIsLoadingHistory(true);
      const response = await apiService.getChatHistory(problemId);
      if (response.success && response.data) {
        setMessages(response.data.messages || []);
        setHasUnsavedChanges(false);
      }
    } catch (error) {
      console.error('Failed to load chat history:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [problemId]);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      });
    }, 100);
  }, []);

  // Load chat history when panel opens
  useEffect(() => {
    if (isOpen && isAuthenticated && problemId) {
      loadChatHistory();
      // Focus input after panel opens
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen, isAuthenticated, problemId, loadChatHistory]);

  // Auto-scroll to bottom when new messages are added
  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages, scrollToBottom]);

  // Reset states when panel closes
  useEffect(() => {
    if (!isOpen) {
      setInputMessage('');
      setError(null);
      setIsLoading(false);
      setIsLoadingHistory(false);
      setHasUnsavedChanges(false);
    }
  }, [isOpen]);

  // Handle Escape key to close
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!inputMessage.trim() || isLoading || !isAuthenticated) {
      return;
    }

    const userMessage: ChatMessage = {
      id: `temp_${Date.now()}`,
      role: 'user',
      content: inputMessage.trim(),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    setError(null);
    setHasUnsavedChanges(true);

    try {
      const response = await apiService.sendChatMessage(problemId, inputMessage.trim());

      if (response.success && response.data) {
        const assistantMessage: ChatMessage = {
          id: `assistant_${Date.now()}`,
          role: 'assistant',
          content: response.data.message,
          timestamp: new Date(),
        };

        setMessages(prev => [...prev, assistantMessage]);

        // Refresh usage to update credits display
        refreshUsage().catch(err => {
          console.warn('Failed to refresh usage after chat:', err);
        });
      } else {
        setError('Failed to send message. Please try again.');
      }
    } catch (error: any) {
      console.error('Failed to send message:', error);

      const isUsageLimitError = error?.status === 402 ||
                                error?.message?.includes('usage-limit-exceeded') ||
                                error?.originalError?.status === 402;

      if (isUsageLimitError) {
        openPaywall('no-credits', 'ai-tutor-chat');
      } else {
        setError(error.message || 'Failed to send message. Please try again.');
      }
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  const copyToClipboard = async (text: string, type: 'message' | 'code', id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'message') {
        setCopiedMessageId(id);
        setTimeout(() => setCopiedMessageId(null), 2000);
      } else {
        setCopiedCodeId(id);
        setTimeout(() => setCopiedCodeId(null), 2000);
      }
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  const formatTimestamp = (timestamp: Date | { _seconds: number; _nanoseconds: number }) => {
    // Handle Firestore Timestamp objects (from backend) which have _seconds and _nanoseconds
    let date: Date;
    if (timestamp && typeof timestamp === 'object' && '_seconds' in timestamp) {
      date = new Date(timestamp._seconds * 1000);
    } else {
      date = new Date(timestamp);
    }

    // Check if date is valid before formatting
    if (isNaN(date.getTime())) {
      return '';
    }

    return date.toLocaleString([], {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <>
      {/* Backdrop */}
      <div
        className="github-chat-backdrop"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Side Panel */}
      <div className="github-chat-side-panel" role="dialog" aria-modal="true" aria-labelledby="chat-title">
        {/* Header */}
        <div className="github-chat-header">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-[#0969da]" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z"/>
              </svg>
              <h3 id="chat-title" className="text-base font-semibold text-[#1f2328]">
                AI Assistant
              </h3>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-md hover:bg-[#eaeef2] transition-colors"
              aria-label="Close chat"
            >
              <svg className="w-4 h-4 text-[#656d76]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Problem Context */}
          <div className="mt-2 text-xs text-[#656d76] truncate">
            <span className="font-medium">Problem:</span> {problemTitle}
          </div>
        </div>

        {/* Messages Area */}
        <div className="github-chat-messages">
          {!isAuthenticated ? (
            <div className="github-chat-empty">
              <svg className="github-chat-empty-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <h4>Sign in required</h4>
              <p>Please sign in to use the AI assistant.</p>
            </div>
          ) : isLoadingHistory ? (
            <div className="github-chat-loading">
              <LoadingSpinner size="small" />
              <span>Loading chat history...</span>
            </div>
          ) : messages.length === 0 ? (
            <div className="github-chat-empty">
              <svg className="github-chat-empty-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
              <h4>👋 Hello! I'm your AI assistant</h4>
              <p>Ask me about algorithms, data structures, patterns, or anything related to the problem: <strong>{problemTitle}</strong></p>
              <p className="github-chat-context-note">Full problem context is loaded — jump right into your question!</p>
            </div>
          ) : (
            messages.map((message) => (
              <div key={message.id} className={`github-message-${message.role}`}>
                {message.role === 'assistant' ? (
                  <>
                    <div className="flex justify-end mb-2">
                      <button
                        onClick={() => copyToClipboard(message.content, 'message', message.id)}
                        className="text-[#656d76] hover:text-[#1f2328] p-1 rounded hover:bg-[#eaeef2] transition-colors"
                        title="Copy message"
                      >
                        {copiedMessageId === message.id ? (
                          <span className="text-[#2da44e]">✓</span>
                        ) : (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        )}
                      </button>
                    </div>
                    <div className="markdown-content">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          code: ({ className, children, ...props }) => {
                            const match = /language-(\w+)/.exec(className || '');
                            const codeContent = String(children).replace(/\n$/, '');
                            const codeId = `${message.id}-${Math.random()}`;

                            return match ? (
                              <div className="relative my-2">
                                <div className="flex justify-between items-center bg-[#24292e] text-[#e6edf3] px-3 py-2 text-xs rounded-t-md">
                                  <span>{match[1]}</span>
                                  <button
                                    onClick={() => copyToClipboard(codeContent, 'code', codeId)}
                                    className="text-[#8b949e] hover:text-[#e6edf3] p-1 rounded hover:bg-[#343942] transition-colors"
                                    title="Copy code"
                                  >
                                    {copiedCodeId === codeId ? (
                                      <span className="text-[#2da44e]">✓</span>
                                    ) : (
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                      </svg>
                                    )}
                                  </button>
                                </div>
                                <pre className="bg-[#1f2328] text-[#e6edf3] p-3 rounded-b-md overflow-x-auto">
                                  <code className={className}>
                                    {children}
                                  </code>
                                </pre>
                              </div>
                            ) : (
                              <code className="bg-[#eaeef2] text-[#cf222e] px-1.5 py-0.5 rounded text-sm">
                                {children}
                              </code>
                            );
                          },
                          pre: ({ children }) => <div>{children}</div>,
                          p: ({ children }) => <p className="mb-2 leading-relaxed">{children}</p>,
                          ul: ({ children }) => <ul className="list-disc list-inside space-y-1 ml-4 mb-2">{children}</ul>,
                          ol: ({ children }) => <ol className="list-decimal list-inside space-y-1 ml-4 mb-2">{children}</ol>,
                          strong: ({ children }) => <strong className="font-semibold text-[#1f2328]">{children}</strong>,
                          h1: ({ children }) => <h1 className="text-lg font-bold mt-3 mb-2 text-[#1f2328]">{children}</h1>,
                          h2: ({ children }) => <h2 className="text-base font-semibold mt-2 mb-1 text-[#1f2328]">{children}</h2>,
                          h3: ({ children }) => <h3 className="text-sm font-semibold mt-2 mb-1 text-[#1f2328]">{children}</h3>,
                        }}
                      >
                        {message.content}
                      </ReactMarkdown>
                    </div>
                  </>
                ) : (
                  <div>{message.content}</div>
                )}
                <div className="text-xs opacity-60 mt-2">
                  {formatTimestamp(message.timestamp)}
                </div>
              </div>
            ))
          )}

          {isLoading && (
            <div className="github-chat-loading">
              <div className="github-chat-loading-dots">
                <div className="github-chat-loading-dot"></div>
                <div className="github-chat-loading-dot"></div>
                <div className="github-chat-loading-dot"></div>
              </div>
              <span>Thinking...</span>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Error Message */}
        {error && (
          <div className="px-4 py-3 bg-[#fff8c5] border-t border-[#d4a72c] text-[#7c4a00] flex items-center justify-between text-sm">
            <span>❌ {error}</span>
            <button
              onClick={() => setError(null)}
              className="text-[#9a6700] hover:text-[#7c4a00] p-1 rounded hover:bg-[#f3e6a8]"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* Input Area */}
        <div className="github-chat-input">
          <form onSubmit={handleSendMessage}>
            <div className="relative">
              <textarea
                ref={inputRef}
                rows={3}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={
                  !isAuthenticated
                    ? "Sign in to chat..."
                    : "Ask about algorithms, patterns, or this problem..."
                }
                disabled={!isAuthenticated || isLoading}
                className="w-full px-3 py-2 pr-20 border border-[#d0d7de] rounded-md
                  focus:outline-none focus:ring-2 focus:ring-[#0969da] focus:border-[#0969da]
                  resize-none text-sm disabled:bg-[#f6f8fa] disabled:cursor-not-allowed"
              />
              <button
                type="submit"
                disabled={!isAuthenticated || isLoading || !inputMessage.trim()}
                className="absolute bottom-2 right-2 px-3 py-1.5 bg-[#0969da]
                  text-white text-sm font-medium rounded-md
                  hover:bg-[#0550ae] disabled:opacity-50 disabled:cursor-not-allowed
                  transition-colors"
              >
                {isLoading ? '...' : 'Send'}
              </button>
            </div>
          </form>

          {/* Tips */}
          <div className="mt-2 text-xs text-[#656d76]">
            Press <kbd className="px-1.5 py-0.5 bg-[#f6f8fa] border border-[#d0d7de] rounded text-xs">Enter</kbd> to send, <kbd className="px-1.5 py-0.5 bg-[#f6f8fa] border border-[#d0d7de] rounded text-xs">Shift+Enter</kbd> for new line
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};
